import { useMemo, useState } from "preact/hooks";
import type { Character } from "../character";
import { emptyFamiliar } from "../character";
import type { DerivedSheet, StatisticView } from "../engine";
import { ATTRIBUTE_LABELS, familiarOverrideTargets, signed } from "../engine";
import type { GuideChange, GuideContext, GuideItem } from "../guide";
import { guideLevels } from "../guide";
import { ItemValue, Picker, filled } from "./builder";
import type { OverrideScope } from "./character-page";
import { useSheetActions } from "./sheet-context";
import type { StatDetail } from "./stat-detail";
import { hitPointsDetail, speedDetail, statisticDetail } from "./stat-detail";
import { ListRow, StatTile, TileRow } from "./tiles";

interface Props {
  character: Character;
  sheet: DerivedSheet;
  ctx: GuideContext | undefined;
  busy: boolean;
  legacy: boolean;
  /** The Character is in play: abilities may be re-chosen at daily preparations, so only an Encounter locks them. */
  sessionOpen: boolean;
  encounter: boolean;
  onChange: (change: GuideChange, label: string) => Promise<void>;
  onSave: (next: Character) => Promise<void>;
  /** Open the statistic panel for one of the Familiar's numbers; its Overrides live on the Familiar record. */
  onStat: (detail: StatDetail, scope: OverrideScope) => void;
}

const ATTRIBUTES = ["str", "dex", "con", "int", "wis", "cha"] as const;

function attributeValue(value: string): (typeof ATTRIBUTES)[number] | null {
  return (ATTRIBUTES as readonly string[]).includes(value) ? (value as (typeof ATTRIBUTES)[number]) : null;
}

/**
 * The Familiar tab (issue #13): the companion's statistics derived from the master with Override
 * controls, its ability slots with the fixed abilities and one picker per free Choice (the same
 * rows the Builder shows at the Level each slot opened), and the answers its abilities ask for.
 * Hidden by the page when nothing gives the Character a Familiar.
 */
export function FamiliarPanel({ character, sheet, ctx, busy, legacy, sessionOpen, encounter, onChange, onSave, onStat }: Props) {
  const { openEntry } = useSheetActions();
  const familiar = sheet.familiar;
  const [editing, setEditing] = useState<string | null>(null);
  const [showLegacy, setShowLegacy] = useState<Set<string>>(new Set());
  const items = useMemo(() => (ctx ? familiarItems(ctx) : []), [ctx]);
  if (!familiar) return null;
  const settings = character.familiar ?? emptyFamiliar();
  const locked = sessionOpen && encounter ? "An Encounter is running: Familiar abilities change at daily preparations." : null;
  const saveFamiliar = (next: Partial<Character["familiar"] & object>) => onSave({ ...character, familiar: { ...settings, ...next } });
  const rename = () => {
    const name = prompt("Name the Familiar", familiar.name)?.trim();
    if (name) void saveFamiliar({ name });
  };
  const scope: OverrideScope = { overrides: settings.overrides, save: (next) => saveFamiliar({ overrides: next }) };
  const statCtx = { sheet: { unsupported: familiar.unsupported, items: familiar.items } };
  const stat = (detail: StatDetail) => onStat(detail, scope);
  const sub = (view: { override: { value: number } | null; derived: number }, dc = false) => (view.override ? `engine ${dc ? view.derived : signed(view.derived)}` : null);
  const change = async (c: GuideChange, label: string) => {
    setEditing(null);
    await onChange(c, label);
  };
  const toggleLegacy = (id: string, on: boolean) => {
    const next = new Set(showLegacy);
    if (on) next.add(id);
    else next.delete(id);
    setShowLegacy(next);
  };
  const speeds = Object.values(familiar.speeds).filter((sp): sp is NonNullable<typeof sp> => sp !== null);
  const iwr = [...familiar.iwr.immunities, ...familiar.iwr.weaknesses, ...familiar.iwr.resistances];
  const fixed = familiar.abilities.filter((a) => a.fixed);
  const byKey = new Map(familiar.abilities.filter((a) => a.choiceKey).map((a) => [a.choiceKey!, a]));

  return (
    <section class="familiar derived">
      <div class="row">
        <h3>Familiar: {familiar.name}</h3>
        <span class="actions">
          <button type="button" disabled={busy} onClick={rename}>Rename</button>
        </span>
      </div>
      <p class="muted">
        Level {familiar.level} · {familiar.size} · {familiar.traits.join(", ")}
        {familiar.senses.length > 0 ? ` · ${familiar.senses.map((s) => s.display ?? s.label).join(", ")}` : ""}
        {familiar.masterDC.spell !== null ? ` · master's spell DC ${familiar.masterDC.spell}` : ""}
        {familiar.masterDC.class !== null ? ` · master's class DC ${familiar.masterDC.class}` : ""}
      </p>
      <div class="inline">
        <label>
          Master's attribute for Perception, Acrobatics and Stealth{" "}
          <select value={settings.attribute ?? ""} disabled={busy} onChange={(e) => void saveFamiliar({ attribute: attributeValue((e.currentTarget as HTMLSelectElement).value) })}>
            <option value="">{ATTRIBUTE_LABELS[familiar.attribute.derived]} (the rules' default)</option>
            {ATTRIBUTES.map((a) => <option key={a} value={a}>{ATTRIBUTE_LABELS[a]}</option>)}
          </select>
        </label>
        <span class="muted">{familiar.attribute.minimum ? `modifier ${signed(familiar.attribute.modifier)}: the +3 minimum applies` : `modifier ${signed(familiar.attribute.modifier)}`}</span>
      </div>
      <TileRow>
        <StatTile label="HP" value={String(familiar.hp.value)} sub={sub(familiar.hp, true)} overridden={!!familiar.hp.override} onTap={() => stat(hitPointsDetail(familiar.hp, statCtx, "Familiar Hit Points"))} />
        <StatTile label="AC" value={String(familiar.ac.value)} sub={sub(familiar.ac, true)} overridden={!!familiar.ac.override} onTap={() => stat(statisticDetail(familiar.ac, statCtx, { dc: true, selectors: ["ac"] }))} />
        {Object.values(familiar.saves).map((s) => <StatTile key={s.slug} label={s.label} value={signed(s.value)} sub={sub(s)} overridden={!!s.override} onTap={() => stat(statisticDetail(s, statCtx, { selectors: [s.slug, "saving-throw"] }))} />)}
        <StatTile label="Perception" value={signed(familiar.perception.value)} sub={sub(familiar.perception)} overridden={!!familiar.perception.override} onTap={() => stat(statisticDetail(familiar.perception, statCtx))} />
        <StatTile label="Attack" value={signed(familiar.attack.value)} sub={sub(familiar.attack)} overridden={!!familiar.attack.override} onTap={() => stat(statisticDetail(familiar.attack, statCtx, { selectors: ["attack", "attack-roll"] }))} />
        {speeds.map((sp) => <StatTile key={sp.type} label={sp.label.replace(" Speed", "")} value={`${sp.value} ft`} sub={sp.override ? `engine ${sp.derived}` : null} overridden={!!sp.override} onTap={() => stat(speedDetail(sp, statCtx))} />)}
      </TileRow>
      {iwr.length > 0 && (
        <p class="muted">
          {familiar.iwr.immunities.length > 0 ? `Immunities: ${familiar.iwr.immunities.map((i) => i.label).join(", ")}. ` : ""}
          {familiar.iwr.weaknesses.length > 0 ? `Weaknesses: ${familiar.iwr.weaknesses.map((i) => i.label).join(", ")}. ` : ""}
          {familiar.iwr.resistances.length > 0 ? `Resistances: ${familiar.iwr.resistances.map((i) => i.label).join(", ")}.` : ""}
        </p>
      )}
      <h4>
        Abilities <span class="muted">{familiar.slots.total} slot{familiar.slots.total === 1 ? "" : "s"}{familiar.slots.fixed > 0 ? `, ${familiar.slots.fixed} fixed` : ""}{familiar.slots.fixedFree > 0 ? `, ${familiar.slots.fixedFree} free of the count` : ""} · {familiar.slots.filled} of {familiar.slots.free} chosen</span>
      </h4>
      {sessionOpen && <p class="notice">{locked ?? "Familiar abilities can be re-chosen during daily preparations (Player Core); the Session stays open."}</p>}
      <ul class="plain abilities">
        {fixed.map((a) => (
          <li key={a.slug}>
            <button type="button" class="linkish item-label" onClick={() => a.uuid && openEntry(a.uuid)}>{a.name}</button>
            <span class="tag">fixed by {a.fixed!.sourceName}{a.fixed!.countsAgainstSlots ? "" : ", free of the count"}</span>
            {a.master && <span class="tag">master ability</span>}
            {!a.loaded && <span class="tag warn">not in this Snapshot</span>}
          </li>
        ))}
        {items.map((item) => {
          const ability = byKey.get(item.choiceKey);
          return (
            <li key={item.id} class={item.open ? "open" : ""}>
              <div class="item-row">
                <span class="item-label">
                  {item.kind === "entry" ? `Level ${item.level}: ${item.label}` : item.label}
                  {item.kind === "answer" ? <span class="muted"> · {item.entryName}</span> : null}
                </span>
                <span class="item-value">
                  <ItemValue item={item} />
                  {item.kind === "entry" && ability?.master && <span class="tag">master ability</span>}
                  {item.open && <span class="tag open">open</span>}
                  {item.warning && <span class="tag warn" title={item.warning}>{item.warning}</span>}
                  {item.kind === "entry" && ability?.extra && <span class="tag warn">no slot opens this Choice now</span>}
                </span>
                <span class="actions">
                  <button type="button" disabled={busy || !!locked} onClick={() => setEditing(editing === item.id ? null : item.id)}>
                    {editing === item.id ? "Close" : filled(item) ? "Change" : "Choose"}
                  </button>
                </span>
              </div>
              {editing === item.id && ctx && (
                <div class="picker">
                  <Picker ctx={ctx} item={item} busy={busy} legacy={legacy || showLegacy.has(item.id)} perChoiceLegacy={showLegacy.has(item.id)} onLegacy={(on) => toggleLegacy(item.id, on)} onChange={change} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <section class="card list">
        <h4>Skills</h4>
        {Object.values(familiar.skills).map((s: StatisticView) => (
          <ListRow key={s.slug} onTap={() => stat(statisticDetail(s, statCtx, { selectors: [s.slug, "skill-check"] }))} class={s.override ? "overridden" : ""}>
            <span class="row-main">{s.label}</span>
            <span class="row-value">{signed(s.value)}{s.override && <span class="stat-derived"> engine {signed(s.derived)}</span>}</span>
          </ListRow>
        ))}
      </section>
      {familiar.unsupported.length > 0 && (
        <p class="notice">Unsupported Rule Elements on the Familiar: {familiar.unsupported.map((u) => `${u.key} (${u.name})`).join(", ")}.</p>
      )}
      {familiar.problems.length > 0 && (
        <p class="notice">Rule Elements that could not be applied: {familiar.problems.map((p) => `${p.name}: ${p.messages.join("; ")}`).join(" · ")}.</p>
      )}
      {familiar.danglingOverrides.length > 0 && (
        <p class="notice">Familiar Overrides naming no statistic: {familiar.danglingOverrides.map((o) => o.statistic).join(", ")}. Known keys: {familiarOverrideTargets(familiar).map((t) => t.key).join(", ")}.</p>
      )}
      <p class="muted">A Familiar has no proficiency ranks or attributes of its own: its modifiers are the master's Level and attribute, and item bonuses never apply.</p>
    </section>
  );
}

/** The guide rows for the Familiar's ability Choices across every Level, each followed by the answers its Entry asks for. */
export function familiarItems(ctx: GuideContext): GuideItem[] {
  const out: GuideItem[] = [];
  for (const level of guideLevels(ctx)) {
    for (const item of level.items) {
      if (item.kind !== "entry" || item.slot !== "familiar-ability") continue;
      out.push(item, ...level.items.filter((i) => i.kind === "answer" && i.choiceKey === item.choiceKey));
    }
  }
  return out;
}

